import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ParagraphS } from "../../../../components/atoms/Typography";
import { Theme } from "../../../../components/atoms/theme";
import { formatDate } from "../../../../utils/formatDate";
import { Badge } from "@tyrannosaurustech/ui";
import { useGetRequestSilentQuery } from "../../../../redux/reduxQuery/requestApi";
import { useGetProviderSilentQuery } from "../../../../redux/reduxQuery/medicalProviderApi";
import { useGetPatientSilentQuery } from "../../../../redux/reduxQuery/patientApi";
import { useGetFirmSilentQuery } from "../../../../redux/reduxQuery/firmApi";
import { useGetUserSilentQuery } from "../../../../redux/reduxQuery/usersApi";

// Component to display provider connection (fromId -> toId)
const ProviderConnectionDisplay = ({
  metadata,
}: {
  metadata?: { fromId?: string; toId?: string };
}) => {
  const navigate = useNavigate();
  const fromId = metadata?.fromId;
  const toId = metadata?.toId;

  const { data: fromProvider, isLoading: fromLoading } =
    useGetProviderSilentQuery(fromId || "", { skip: !fromId });
  const { data: toProvider, isLoading: toLoading } = useGetProviderSilentQuery(
    toId || "",
    { skip: !toId }
  );

  const isLoading = fromLoading || toLoading;

  if (isLoading) {
    return (
      <ParagraphS style={{ margin: 0, color: Theme.colors.gray_000 }}>
        Loading...
      </ParagraphS>
    );
  }

  const fromName =
    (fromProvider as any)?.name ||
    (fromId ? `${fromId.substring(0, 6)}...` : "Unknown");
  const toName =
    (toProvider as any)?.name ||
    (toId ? `${toId.substring(0, 6)}...` : "Unknown");

  const handleFromClick = (e: React.MouseEvent) => {
    if (fromId) {
      e.preventDefault();
      navigate(`/admin/medical-providers/${fromId}`);
    }
  };

  const handleToClick = (e: React.MouseEvent) => {
    if (toId) {
      e.preventDefault();
      navigate(`/admin/medical-providers/${toId}`);
    }
  };

  return (
    <ParagraphS style={{ margin: 0 }}>
      <a
        href={fromId ? `/admin/medical-providers/${fromId}` : "#"}
        onClick={handleFromClick}
        style={{
          color: Theme.colors.primaries_000 || "#6c5ce7",
          textDecoration: "underline",
          cursor: "pointer",
          fontWeight: 500,
        }}
        title={fromId}
      >
        {fromName}
      </a>
      <span style={{ margin: "0 0.5rem", color: Theme.colors.neutrals_000 }}>
        →
      </span>
      <a
        href={toId ? `/admin/medical-providers/${toId}` : "#"}
        onClick={handleToClick}
        style={{
          color: Theme.colors.primaries_000 || "#6c5ce7",
          textDecoration: "underline",
          cursor: "pointer",
          fontWeight: 500,
        }}
        title={toId}
      >
        {toName}
      </a>
    </ParagraphS>
  );
};

// Component to display entity name based on entity type
const EntityNameDisplay = ({
  entityType,
  entityId,
  metadata,
  eventType,
  requestId,
}: {
  entityType: string;
  entityId: string;
  metadata?: any;
  eventType?: string;
  requestId?: string;
}) => {
  const navigate = useNavigate();

  // For DELETE and CREATE events, check metadata first (DELETE: entity no longer exists, CREATE: entity data is in metadata)
  const isDeleteEvent = eventType === "DELETE";
  const isCreateEvent = eventType === "CREATE";

  // Try to get entity data from metadata first (for DELETE and CREATE events)
  let entityFromMetadata: any = null;
  if (metadata && (isDeleteEvent || isCreateEvent)) {
    switch (entityType) {
      case "REQUEST":
        entityFromMetadata = metadata.deletedRequest || metadata.request;
        break;
      case "PROVIDER":
        entityFromMetadata = metadata.provider;
        break;
      case "PATIENT":
        entityFromMetadata = metadata.patient;
        break;
      case "FIRM":
        entityFromMetadata = metadata.firm;
        break;
      case "USER":
        entityFromMetadata = metadata.user;
        break;
    }
  }

  // Skip fetching if entityId is "bulk" (used for BULK_UPDATE events - not a real entity ID)
  const isBulkEntity = entityId === "bulk";

  // For MEDICAL_REQUEST, we fetch using the parent requestId instead of entityId
  const isMedicalRequest = entityType === "MEDICAL_REQUEST";
  const requestIdToFetch = isMedicalRequest ? requestId : entityId;

  // Only fetch from API if not a DELETE/CREATE event or if metadata doesn't have the entity
  const shouldSkipRequest =
    (entityType !== "REQUEST" && !isMedicalRequest) ||
    isBulkEntity ||
    (isMedicalRequest && !requestId) ||
    ((isDeleteEvent || isCreateEvent) && entityFromMetadata);
  const shouldSkipProvider =
    entityType !== "PROVIDER" ||
    isBulkEntity ||
    ((isDeleteEvent || isCreateEvent) && entityFromMetadata);
  const shouldSkipPatient =
    entityType !== "PATIENT" ||
    isBulkEntity ||
    ((isDeleteEvent || isCreateEvent) && entityFromMetadata);
  const shouldSkipFirm =
    entityType !== "FIRM" ||
    isBulkEntity ||
    ((isDeleteEvent || isCreateEvent) && entityFromMetadata);
  const shouldSkipUser =
    entityType !== "USER" ||
    isBulkEntity ||
    ((isDeleteEvent || isCreateEvent) && entityFromMetadata);

  const { data: request, isLoading: requestLoading } = useGetRequestSilentQuery(
    requestIdToFetch || "",
    { skip: shouldSkipRequest }
  );
  const { data: provider, isLoading: providerLoading } =
    useGetProviderSilentQuery(entityId, { skip: shouldSkipProvider });
  const { data: patient, isLoading: patientLoading } = useGetPatientSilentQuery(
    entityId,
    { skip: shouldSkipPatient }
  );
  const { data: firm, isLoading: firmLoading } = useGetFirmSilentQuery(
    entityId,
    {
      skip: shouldSkipFirm,
    }
  );
  const { data: user, isLoading: userLoading } = useGetUserSilentQuery(
    entityId,
    {
      skip: shouldSkipUser,
    }
  );

  const isLoading =
    requestLoading ||
    providerLoading ||
    patientLoading ||
    firmLoading ||
    userLoading;

  if (isLoading && !entityFromMetadata) {
    return (
      <ParagraphS style={{ margin: 0, color: Theme.colors.gray_000 }}>
        Loading...
      </ParagraphS>
    );
  }

  let displayName: string | null = null;
  let navigatePath: string | null = null;

  // Use metadata entity if available (for DELETE events), otherwise use fetched entity
  const finalRequest =
    entityFromMetadata && entityType === "REQUEST"
      ? entityFromMetadata
      : request;
  const finalProvider =
    entityFromMetadata && entityType === "PROVIDER"
      ? entityFromMetadata
      : provider;
  const finalPatient =
    entityFromMetadata && entityType === "PATIENT"
      ? entityFromMetadata
      : patient;
  const finalFirm =
    entityFromMetadata && entityType === "FIRM" ? entityFromMetadata : firm;
  const finalUser =
    entityFromMetadata && entityType === "USER" ? entityFromMetadata : user;

  switch (entityType) {
    case "REQUEST":
      // Handle bulk update events specially
      if (isBulkEntity) {
        displayName = "Bulk Update";
        break;
      }
      if (finalRequest) {
        const firmName =
          finalRequest.firm?.name || metadata?.firm?.name || "Unknown Firm";
        const patientName =
          finalRequest.patient?.name ||
          metadata?.patient?.name ||
          "Unknown Patient";
        displayName = `${firmName} - ${patientName}`;
        // Don't make it clickable if it's deleted
        if (!isDeleteEvent) {
          navigatePath = `/admin/requests/${entityId}`;
        }
      }
      break;
    case "PROVIDER":
      if (finalProvider) {
        displayName = finalProvider.name || finalProvider.id?.substring(0, 8);
        if (!isDeleteEvent) {
          navigatePath = `/admin/medical-providers/${entityId}`;
        }
      }
      break;
    case "PATIENT":
      if (finalPatient) {
        displayName = finalPatient.name || finalPatient.id?.substring(0, 8);
        if (!isDeleteEvent) {
          navigatePath = `/admin/patients/${entityId}`;
        }
      }
      break;
    case "FIRM":
      if (finalFirm && finalFirm.id) {
        displayName = finalFirm.name || finalFirm.id?.substring(0, 8);
        if (!isDeleteEvent) {
          navigatePath = `/admin/law-firms/${finalFirm.id}`;
        }
      }
      break;
    case "USER":
      if (finalUser) {
        displayName =
          finalUser.name || finalUser.email || finalUser.id?.substring(0, 8);
        if (!isDeleteEvent) {
          navigatePath = `/admin/users/${entityId}`;
        }
      }
      break;
    case "MEDICAL_REQUEST":
      // Medical requests - show parent request's firm + patient if available
      if (finalRequest && requestId) {
        const firmName = finalRequest.firm?.name || "Unknown Firm";
        const patientName = finalRequest.patient?.name || "Unknown Patient";
        displayName = `${firmName} - ${patientName}`;
        navigatePath = `/admin/requests/${requestId}`;
      } else {
        displayName = entityId.substring(0, 8) + "...";
      }
      break;
    case "PROVIDER_CONNECTION":
      // Provider connections - use special component to show from -> to
      return <ProviderConnectionDisplay metadata={metadata} />;
    default:
      displayName = entityId.substring(0, 8) + "...";
  }

  if (!displayName) {
    displayName = entityId.substring(0, 8) + "...";
  }

  const handleClick = (e: React.MouseEvent) => {
    if (navigatePath) {
      e.preventDefault();
      navigate(navigatePath);
    }
  };

  if (navigatePath) {
    return (
      <a
        href={navigatePath}
        onClick={handleClick}
        style={{
          color: Theme.colors.primaries_000 || "#6c5ce7",
          textDecoration: "underline",
          cursor: "pointer",
          fontWeight: 500,
        }}
        title={entityId}
      >
        <ParagraphS
          style={{ margin: 0, color: Theme.colors.primaries_000 || "#6c5ce7" }}
        >
          {displayName}
        </ParagraphS>
      </a>
    );
  }

  return (
    <ParagraphS
      style={{ margin: 0, color: Theme.colors.neutrals_000 }}
      title={entityId}
    >
      {displayName}
    </ParagraphS>
  );
};

interface AuditLogDetail {
  id: string;
  metadata: any;
  message: string;
  createdAt?: string;
  eventType?: string;
  entityType?: string;
  userName?: string;
}

interface UseColumnsReturn {
  columns: any[];
  selectedAuditLog: AuditLogDetail | null;
  setSelectedAuditLog: (log: AuditLogDetail | null) => void;
}

export const useColumns = (): UseColumnsReturn => {
  const [selectedAuditLog, setSelectedAuditLog] =
    useState<AuditLogDetail | null>(null);

  return {
    columns: useMemo(
      () => [
        {
          Header: "Timestamp",
          header: "Timestamp",
          accessorKey: "createdAt",
          size: 180,
          cell: (cell: any) => {
            return formatDate(cell.row.original.createdAt);
          },
          disableFilters: true,
        },
        {
          Header: "User",
          header: "User",
          accessor: "userName",
          accessorKey: "userName",
          size: 200,
          filterName: "userName",
          includeToolTip: true,
        },
        {
          Header: "Event Type",
          header: "Event Type",
          accessorKey: "eventType",
          size: 180,
          disableFilters: true,
          cell: (cell: any) => {
            const eventType = cell.row.original.eventType;
            return (
              <Badge
                label={eventType.replace(/_/g, " ")}
                color={Theme.colors.primaries_000}
                rounded={true}
              />
            );
          },
        },
        {
          Header: "Entity Type",
          header: "Entity Type",
          accessorKey: "entityType",
          size: 150,
          disableFilters: true,
          cell: (cell: any) => {
            return cell.row.original.entityType.replace(/_/g, " ");
          },
        },
        {
          Header: "Entity",
          header: "Entity",
          accessorKey: "entityId",
          size: 250,
          filterName: "entityId",
          includeToolTip: true,
          cell: (cell: any) => {
            const entityType = cell.row.original.entityType;
            const entityId = cell.row.original.entityId;
            const metadata = cell.row.original.metadata;
            const eventType = cell.row.original.eventType;
            const requestId = cell.row.original.requestId;
            return (
              <EntityNameDisplay
                entityType={entityType}
                entityId={entityId}
                metadata={metadata}
                eventType={eventType}
                requestId={requestId}
              />
            );
          },
        },
        {
          Header: "Patient",
          header: "Patient",
          accessor: "patientName",
          accessorKey: "patientName",
          size: 150,
          filterName: "patientName",
          includeToolTip: true,
          cell: (cell: any) => {
            const name = cell.row.original.patientName;
            if (!name)
              return (
                <ParagraphS style={{ margin: 0, color: Theme.colors.gray_000 }}>
                  -
                </ParagraphS>
              );
            return <ParagraphS style={{ margin: 0 }}>{name}</ParagraphS>;
          },
        },
        {
          Header: "Provider",
          header: "Provider",
          accessor: "providerName",
          accessorKey: "providerName",
          size: 150,
          filterName: "providerName",
          includeToolTip: true,
          cell: (cell: any) => {
            const name = cell.row.original.providerName;
            if (!name)
              return (
                <ParagraphS style={{ margin: 0, color: Theme.colors.gray_000 }}>
                  -
                </ParagraphS>
              );
            return <ParagraphS style={{ margin: 0 }}>{name}</ParagraphS>;
          },
        },
        {
          Header: "Firm",
          header: "Firm",
          accessor: "firmName",
          accessorKey: "firmName",
          size: 150,
          filterName: "firmName",
          includeToolTip: true,
          cell: (cell: any) => {
            const name = cell.row.original.firmName;
            if (!name)
              return (
                <ParagraphS style={{ margin: 0, color: Theme.colors.gray_000 }}>
                  -
                </ParagraphS>
              );
            return <ParagraphS style={{ margin: 0 }}>{name}</ParagraphS>;
          },
        },
        {
          Header: "Message",
          header: "Message",
          accessorKey: "message",
          size: 300,
          cell: (cell: any) => {
            return (
              <ParagraphS style={{ margin: 0 }}>
                {cell.row.original.message}
              </ParagraphS>
            );
          },
        },
        {
          Header: "Details",
          header: "Details",
          accessorKey: "metadata",
          size: 150,
          disableFilters: true,
          enableSorting: false,
          cell: (cell: any) => {
            const hasMetadata = cell.row.original.metadata;
            if (!hasMetadata) {
              return (
                <ParagraphS style={{ margin: 0, color: Theme.colors.gray_000 }}>
                  No details
                </ParagraphS>
              );
            }
            return (
              <>
                <button
                  onClick={() =>
                    setSelectedAuditLog({
                      id: cell.row.original.id,
                      metadata: cell.row.original.metadata,
                      message: cell.row.original.message,
                      createdAt: cell.row.original.createdAt,
                      eventType: cell.row.original.eventType,
                      entityType: cell.row.original.entityType,
                      userName: cell.row.original.userName,
                    })
                  }
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: Theme.colors.primaries_000,
                    textDecoration: "underline",
                  }}
                >
                  Show details
                </button>
              </>
            );
          },
        },
      ],
      []
    ),
    selectedAuditLog,
    setSelectedAuditLog,
  };
};
